<template>
  <div class="sales-dashboard" :class="{ 'sales-dashboard--dark': isDarkSkin }">

    <div class="sales-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard v-model="filter" v-bind:fields="filterFields"
                   v-bind:hidden-fields="{'buTypes': viewKey!=='sales-dashboard' }"/>
      <calendar-bar v-model="dateRange" @input="scheduleRefresh"/>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <b-row class="kpi-row">
        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--primary">
            <div class="kpi-card__icon">
              <feather-icon icon="UsersIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('FTD / Total Clients') }}</div>
              <div class="kpi-card__value">{{ statistics.ftdsCount }} <span class="kpi-card__value-sep">/</span> {{ statistics.clientsCount }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="WifiIcon" size="12" class="mr-25"/>
                {{ $t('Online') }}: {{ statistics.onlineClients }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--success">
            <div class="kpi-card__icon">
              <feather-icon icon="DollarSignIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Total Deposits') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalDeposits) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="TrendingUpIcon" size="12" class="mr-25"/>
                {{ $t('Net') }}: {{ convertToEURO(statistics.netDeposits) }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--warning">
            <div class="kpi-card__icon">
              <feather-icon icon="ActivityIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Closed P&L') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(closedPL) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="BarChart2Icon" size="12" class="mr-25"/>
                {{ $t('Open P&L') }}: {{ convertToEURO(openPL) }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--danger">
            <div class="kpi-card__icon">
              <feather-icon icon="ArrowDownCircleIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Withdrawals') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalWithdrawals) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="PercentIcon" size="12" class="mr-25"/>
                {{ $t('Rate') }}: {{ withdrawalRate }}%
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
      </b-row>

      <b-row class="mt-1">
        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip">
                  <feather-icon icon="UserIcon" size="18"/>
                </div>
                <h5 class="panel-card__title mb-0">{{ $t('Deposits by Sales Agent') }}</h5>
              </div>
              <div class="panel-card__toggle">
                <span :class="{'text-muted': showUserDeposit}">€</span>
                <b-form-checkbox switch v-model="showUserDeposit" class="mx-50"/>
                <span :class="{'text-muted': !showUserDeposit}">#</span>
              </div>
            </div>
            <div class="panel-card__body">
              <user-sales-bar-chart :chart-data="userDepositData" :currency-format="showUserDeposit" :loading="loading" :is-dark-skin="isDarkSkin"/>
            </div>
          </div>
        </b-col>

        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip">
                  <feather-icon icon="GlobeIcon" size="18"/>
                </div>
                <h5 class="panel-card__title mb-0">{{ $t('Deposits by Country') }}</h5>
              </div>
              <div class="panel-card__toggle">
                <span :class="{'text-muted': showCountryDeposit}">€</span>
                <b-form-checkbox switch v-model="showCountryDeposit" class="mx-50"/>
                <span :class="{'text-muted': !showCountryDeposit}">#</span>
              </div>
            </div>
            <div class="panel-card__body">
              <country-deposits-bar-chart :chart-data="countryDepositData" :currency-format="showCountryDeposit" :loading="loading" :is-dark-skin="isDarkSkin"/>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row class="mt-1">
        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip panel-card__icon-chip--alt">
                  <feather-icon icon="PieChartIcon" size="18"/>
                </div>
                <h5 class="panel-card__title mb-0">{{ $t('Clients by Status') }}</h5>
              </div>
            </div>
            <div class="panel-card__body">
              <client-status-pie-chart :chart-data="statistics.statusMapCount" :loading="loading" :is-dark-skin="isDarkSkin"/>
            </div>
          </div>
        </b-col>

        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip panel-card__icon-chip--alt">
                  <feather-icon icon="MapPinIcon" size="18"/>
                </div>
                <h5 class="panel-card__title mb-0">{{ $t('Clients by Country') }}</h5>
              </div>
            </div>
            <div class="panel-card__body">
              <client-country-pie-chart :chart-data="statistics.clientCountryMapCount" :loading="loading" :is-dark-skin="isDarkSkin"/>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import DashboardSearchRequest from '@/model/Dashboard/DashboardSearchRequest';
import FilterCard from '@/components/widget/FilterCard';
import SalesDashboardFilterFields from '@/model/filter/SalesDashboardFilterFields';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import UserSalesBarChart from '@/components/chart/UserSalesBarChart.vue';
import CountryDepositsBarChart from '@/components/chart/CountryDepositsBarChart.vue';
import ClientStatusPieChart from '@/components/chart/ClientStatusPieChart.vue';
import ClientCountryPieChart from '@/components/chart/ClientCountryPieChart.vue';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'SalesDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],

  components: {
    ClientCountryPieChart,
    ClientStatusPieChart,
    CountryDepositsBarChart,
    UserSalesBarChart,
    CalendarBar,
    FilterCard,
  },

  data: () => ({
    filter: DashboardSearchRequest(),
    filterFields: [],
    dateRange: { from: '', until: '' },
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    statistics: {
      totalDeposits: 0,
      netDeposits: 0,
      totalWithdrawals: 0,
      onlineClients: 0,
      clientsCount: 0,
      ftdsCount: 0,
      userDeposit: {},
      userCount: {},
      countryDeposit: {},
      countryCount: {},
      statusMapCount: {},
      clientCountryMapCount: {},
    },
    closedPL: 0,
    openPL: 0,
    showUserDeposit: false,
    showCountryDeposit: false,
  }),
  watch: {
    filter: {
      handler: 'scheduleRefresh',
      deep: true,
    },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    viewKey() {
      return this.$route.name || 'sales-dashboard';
    },
    userDepositData() {
      return !this.showUserDeposit ? this.statistics.userDeposit : this.statistics.userCount;
    },
    countryDepositData() {
      return !this.showCountryDeposit ? this.statistics.countryDeposit : this.statistics.countryCount;
    },
    withdrawalRate() {
      const deposits = this.statistics.totalDeposits || 0;
      const withdrawals = this.statistics.totalWithdrawals || 0;
      if (!deposits) return '0.00';
      return (withdrawals / deposits * -100).toFixed(2);
    }
  },
  created() {
    this.filterFields = SalesDashboardFilterFields();
  },
  mounted() {
    this.$root.$on('dashboard::refresh', this.scheduleRefresh);
  },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  },
  methods: {
    ...mapActions('dashboard', ['getSalesDashboard']),
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (this.inflight) {
        this.pendingFetch = true;
        return;
      }
      this.doFetch();
    },
    doFetch() {
      const payload = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
      };
      this.inflight = true;
      this.loading = true;
      this.getSalesDashboard(payload)
        .then(
          data => this.setData(data),
          err => this.showServerErrorNotification('getSalesDashboard', 'dashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) {
            this.pendingFetch = false;
            this.doFetch();
          }
        });
    },
    setData(data) {
      if (!data) return;
      this.statistics.totalDeposits = data.totalDeposits || 0;
      this.statistics.netDeposits = data.netDeposits || 0;
      this.statistics.totalWithdrawals = data.totalWithdrawals || 0;
      this.statistics.onlineClients = data.clientsOnline || 0;
      this.statistics.ftdsCount = data.ftdsCount || 0;
      this.statistics.clientsCount = data.clientsCount || 0;
      this.statistics.userDeposit = data.userDeposit || {};
      this.statistics.userCount = data.userCount || {};
      this.statistics.countryDeposit = data.countryDeposit || {};
      this.statistics.countryCount = data.countryCount || {};
      this.statistics.statusMapCount = data.statusMapCount || {};
      this.statistics.clientCountryMapCount = data.clientCountryMapCount || {};
      this.closedPL = data.closedPL || 0;
    },
  }
};
</script>

<style scoped lang="scss">
.sales-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.kpi-row {
  margin-top: 0.5rem;
}

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28),
              0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35),
                0 4px 8px rgba(20, 24, 50, 0.08);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.82rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.86;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value-sep {
    opacity: 0.55;
    font-weight: 500;
    margin: 0 0.15rem;
  }

  &__footer {
    margin-top: 0.45rem;
    font-size: 0.76rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
  }

  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  &--primary {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0056b3 100%);
  }

  &--success {
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }

  &--warning {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%);
  }

  &--danger {
    background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%);
  }
}

.panel-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 24px -16px rgba(20, 24, 50, 0.22),
              0 1px 3px rgba(20, 24, 50, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px -18px rgba(20, 24, 50, 0.28),
                0 2px 4px rgba(20, 24, 50, 0.05);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(110, 117, 159, 0.12);
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  &__icon-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(0, 123, 255, 0.14), rgba(0, 123, 255, 0.04));
    color: #007BFF;

    &--alt {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(16, 185, 129, 0.04));
      color: #10b981;
    }
  }

  &__title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #1f2a44;
  }

  &__toggle {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 0.95rem;
  }

  &__body {
    padding: 0.75rem 1rem 1rem;
    flex: 1;
  }
}

.sales-dashboard--dark {
  .panel-card {
    background: #283046;
    box-shadow: 0 10px 24px -16px rgba(0, 0, 0, 0.6);
  }

  .panel-card__header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .panel-card__title {
    color: #d0d2d6;
  }

  .panel-card__icon-chip {
    background: linear-gradient(135deg, rgba(77, 163, 255, 0.25), rgba(77, 163, 255, 0.05));
    color: #a8b6ff;

    &--alt {
      background: linear-gradient(135deg, rgba(46, 213, 162, 0.25), rgba(46, 213, 162, 0.05));
      color: #6bdcb1;
    }
  }
}

@media (max-width: 576px) {
  .kpi-card {
    min-height: auto;
    padding: 1rem;
    &__value { font-size: 1.3rem; }
  }
}
</style>
